import {
  formatRusadaTechLogNo,
  formatRusadaTechLogTimestamp,
} from '../tech-log-no';
import { TechLogToRusadaFlightInput } from './validation';

/**
 * Transformation schema for TechLog sectors to Rusada TechLogs.
 */
export const techlogFlightToRusadaSchema = {
  // Composite Aircraft Log No keeps the trip number recognisable against the eTL Trip Log
  // while the route, takeoff timestamp, and short sector id ensure uniqueness across
  // multiple sectors in the same trip (Veryon rejects duplicate TechLog numbers).
  techLogNo: (input: TechLogToRusadaFlightInput) => {
    const dep = input.rusada?.airportDeparture?.icaoCode || '?';
    const arr = input.rusada?.airportArrival?.icaoCode || '?';
    return formatRusadaTechLogNo({
      tripNumber: input.techlog?.trip?.number,
      entityId: input.techlog?.sector?.id,
      alternateTripId: input.techlog?.trip?.id,
      instantPrefix: 'S',
      middleParts: [
        `${dep}-${arr}`,
        formatRusadaTechLogTimestamp(input.techlog?.sector?.takeoffTime),
      ],
    });
  },

  // TechLog date in UTC - use takeoff time, landing time, or trip date
  techLogDateUtc: (input: TechLogToRusadaFlightInput) => {
    const takeoffTime = input.techlog?.sector?.takeoffTime;
    const landingTime = input.techlog?.sector?.landingTime;
    const flightTime = takeoffTime || landingTime;

    if (flightTime) {
      return new Date(flightTime).toISOString();
    }

    // Fallback to trip date if available
    const tripDate = input.techlog?.trip?.date;
    return tripDate
      ? new Date(tripDate).toISOString()
      : new Date().toISOString();
  },

  // Registration ID from Rusada registration lookup
  registrationId: (input: TechLogToRusadaFlightInput) =>
    input.rusada?.registration?.id,

  // Select pure TechLog category (isTechLog only), fallback to any isTechLog=true
  // FIXME: Could be a temporary fix, simplify once configuration is finalized.
  techLogCategoryId: (input: TechLogToRusadaFlightInput) => {
    const categories = input.rusada?.techLogCategories || [];
    // Prefer pure TechLog category (only isTechLog=true, nothing else)
    const pureTechLog = categories.find(
      (cat) =>
        cat.isTechLog === true &&
        cat.isMaintenanceEntry !== true &&
        cat.isCabinLog !== true,
    );
    if (pureTechLog) {
      return pureTechLog.id;
    }
    // Fallback to any category with isTechLog=true
    const techLogCategory = categories.find((cat) => cat.isTechLog === true);
    return techLogCategory?.id ?? 1;
  },

  // Sectors array - required, must have at least one sector
  sectors: (input: TechLogToRusadaFlightInput) => {
    const sector = input.techlog?.sector;
    if (!sector) {
      return [];
    }

    // Calculate flight hours in minutes from takeoff/landing times
    let flightMinutes = 0;
    if (sector.takeoffTime && sector.landingTime) {
      const takeoff = new Date(sector.takeoffTime);
      const landing = new Date(sector.landingTime);
      const diffMs = landing.getTime() - takeoff.getTime();
      flightMinutes = Math.round(diffMs / 1000 / 60); // Convert to minutes
    }

    const departurePlaceId = input.rusada?.airportDeparture?.id;
    // FIXME: NOT SURE ABOUT THIS LOGIC, CHECK WITH VERYON KNOWHOW
    const arrivalPlaceId = sector.actualArrivalAirportId
      ? input.rusada?.airportActualArrival?.id
      : input.rusada?.airportArrival?.id;

    // Resolve flightTypeId from sortie_profile_code
    const sortieProfileCode = sector.postflightCustomData?.sortie_profile_code;
    const flightTypes = input.rusada?.flightTypes;
    let flightTypeId: number | undefined;
    if (typeof sortieProfileCode === 'string' && flightTypes?.length) {
      // FIXME: Affinity's sortie_profile_code custom field on TechLog has a numeric prefix (e.g., "4_Phenom SPC 01").
      // This should be cleaned up at the TechLog source. Remove this stripping once fixed.
      const strippedSPC = sortieProfileCode.replace(/^\d+_/, '');
      const matchingType = flightTypes.find(
        (ft) => ft.type.toUpperCase() === strippedSPC.toUpperCase(),
      );
      flightTypeId = matchingType?.id;
    }

    // Landings and aero from postflightCustomData and sector fields
    const rollLandings =
      (sector.postflightCustomData?.roll as number | undefined) ?? 0;
    const fsLandings =
      (sector.postflightCustomData?.stop as number | undefined) ?? 0;
    const aeroMinutes =
      (sector.postflightCustomData?.aero as number | undefined) ?? 0;
    const totalLandings = sector.landingsCount ?? 0;
    const cycles = sector.cycles?.airframe ?? fsLandings;
    // Per-engine starts come from TechLog postflight custom fields (RH_ES/LH_ES).
    // TechLog reports a field that is configured on the aircraft but left empty as
    // null, and Veryon requires a numeric usage, so only a number counts as a value.
    const rhEngineStarts =
      typeof sector.postflightCustomData?.RH_ES === 'number'
        ? sector.postflightCustomData.RH_ES
        : undefined;
    const lhEngineStarts =
      typeof sector.postflightCustomData?.LH_ES === 'number'
        ? sector.postflightCustomData.LH_ES
        : undefined;
    // POB: Pilots + Additional Crew + Passengers
    const pilotCount =
      (sector.pilotInCommandId ? 1 : 0) + (sector.secondInCommandId ? 1 : 0);
    const additionalCrewCount = input.techlog?.additionalCrew?.length ?? 0;
    const passengerCount =
      (sector.passengersMale ?? 0) +
      (sector.passengersFemale ?? 0) +
      (sector.passengersChildren ?? 0) +
      (sector.passengersInfants ?? 0);
    const pob = pilotCount + additionalCrewCount + passengerCount;

    const registrationLifeCodes = input.rusada?.registrationLifeCodes;
    const lifeCodes: Array<{
      locationId: number;
      locationPositionId: number;
      lifeCodeId: number;
      usage: number;
    }> = [];

    if (registrationLifeCodes?.length) {
      const findLifeCode = (
        lifeCodeName: string,
        locationName: string,
        positionName: string,
      ) => {
        return registrationLifeCodes.find(
          (lc) =>
            lc.lifeCode.toUpperCase() === lifeCodeName.toUpperCase() &&
            lc.location.toUpperCase() === locationName.toUpperCase() &&
            lc.locationPosition.toUpperCase() === positionName.toUpperCase(),
        );
      };

      // AIRFRAME lifecodes
      const airframeFlightHours = findLifeCode('FLIGHT HOURS', 'AIRFRAME', '');
      if (airframeFlightHours) {
        lifeCodes.push({
          locationId: airframeFlightHours.locationId,
          locationPositionId: airframeFlightHours.locationPositionId,
          lifeCodeId: airframeFlightHours.lifeCodeId,
          usage: flightMinutes,
        });
      }

      // AERO HOURS from TechLog postflight custom field
      const airframeAeroHours = findLifeCode('AERO HOURS', 'AIRFRAME', '');
      if (airframeAeroHours) {
        lifeCodes.push({
          locationId: airframeAeroHours.locationId,
          locationPositionId: airframeAeroHours.locationPositionId,
          lifeCodeId: airframeAeroHours.lifeCodeId,
          usage: aeroMinutes,
        });
      }

      const airframeTotalLandings = findLifeCode(
        'TOTAL LANDINGS',
        'AIRFRAME',
        '',
      );
      if (airframeTotalLandings) {
        lifeCodes.push({
          locationId: airframeTotalLandings.locationId,
          locationPositionId: airframeTotalLandings.locationPositionId,
          lifeCodeId: airframeTotalLandings.lifeCodeId,
          usage: totalLandings,
        });
      }

      const airframeRollLandings = findLifeCode(
        'ROLL LANDINGS',
        'AIRFRAME',
        '',
      );
      if (airframeRollLandings) {
        lifeCodes.push({
          locationId: airframeRollLandings.locationId,
          locationPositionId: airframeRollLandings.locationPositionId,
          lifeCodeId: airframeRollLandings.lifeCodeId,
          usage: rollLandings,
        });
      }

      const airframeFsLandings = findLifeCode('FS LANDINGS', 'AIRFRAME', '');
      if (airframeFsLandings) {
        lifeCodes.push({
          locationId: airframeFsLandings.locationId,
          locationPositionId: airframeFsLandings.locationPositionId,
          lifeCodeId: airframeFsLandings.lifeCodeId,
          usage: fsLandings,
        });
      }

      const airframePob = findLifeCode('POB', 'AIRFRAME', '');
      if (airframePob) {
        lifeCodes.push({
          locationId: airframePob.locationId,
          locationPositionId: airframePob.locationPositionId,
          lifeCodeId: airframePob.lifeCodeId,
          usage: pob,
        });
      }

      // Airframe-level Engine Starts take the RH (No2) engine-start count, which is
      // applied to both aircraft batteries (SS-1295). Emitted only when the RH
      // custom field is present, mirroring the per-engine start guard below.
      const airframeEngineStarts = findLifeCode(
        'Engine Starts',
        'AIRFRAME',
        '',
      );
      if (airframeEngineStarts && rhEngineStarts !== undefined) {
        lifeCodes.push({
          locationId: airframeEngineStarts.locationId,
          locationPositionId: airframeEngineStarts.locationPositionId,
          lifeCodeId: airframeEngineStarts.lifeCodeId,
          usage: rhEngineStarts,
        });
      }

      // 72-00 SINGLE ENGINE lifecodes (no position)
      // These appear on single-engine aircraft (T-6C Texan)
      const engineCyclesSingle = findLifeCode('ENGINE CYCLES', '72-00', '');
      if (engineCyclesSingle) {
        lifeCodes.push({
          locationId: engineCyclesSingle.locationId,
          locationPositionId: engineCyclesSingle.locationPositionId,
          lifeCodeId: engineCyclesSingle.lifeCodeId,
          usage: cycles,
        });
      }

      const engineHours = findLifeCode('ENGINE HOURS', '72-00', '');
      if (engineHours) {
        lifeCodes.push({
          locationId: engineHours.locationId,
          locationPositionId: engineHours.locationPositionId,
          lifeCodeId: engineHours.lifeCodeId,
          usage: flightMinutes, // Engine hours = flight hours
        });
      }

      // 72-00 TWIN ENGINE lifecodes (RH and LH positions)
      // These appear on twin-engine aircraft (Phenom EMB-500)
      // For twin engines, all values mirror the airframe values per business requirement
      for (const positionName of ['RH', 'LH']) {
        const twinFlightHours = findLifeCode(
          'FLIGHT HOURS',
          '72-00',
          positionName,
        );
        if (twinFlightHours) {
          lifeCodes.push({
            locationId: twinFlightHours.locationId,
            locationPositionId: twinFlightHours.locationPositionId,
            lifeCodeId: twinFlightHours.lifeCodeId,
            usage: flightMinutes,
          });
        }

        const twinTotalLandings = findLifeCode(
          'TOTAL LANDINGS',
          '72-00',
          positionName,
        );
        if (twinTotalLandings) {
          lifeCodes.push({
            locationId: twinTotalLandings.locationId,
            locationPositionId: twinTotalLandings.locationPositionId,
            lifeCodeId: twinTotalLandings.lifeCodeId,
            usage: totalLandings,
          });
        }

        const twinEngineStarts = findLifeCode(
          'Engine Starts',
          '72-00',
          positionName,
        );
        const positionEngineStarts =
          positionName === 'RH' ? rhEngineStarts : lhEngineStarts;
        if (twinEngineStarts && positionEngineStarts !== undefined) {
          lifeCodes.push({
            locationId: twinEngineStarts.locationId,
            locationPositionId: twinEngineStarts.locationPositionId,
            lifeCodeId: twinEngineStarts.lifeCodeId,
            usage: positionEngineStarts,
          });
        }

        const twinEngineCyclesPhenom = findLifeCode(
          'Engine Cycles Phenom',
          '72-00',
          positionName,
        );
        if (twinEngineCyclesPhenom) {
          lifeCodes.push({
            locationId: twinEngineCyclesPhenom.locationId,
            locationPositionId: twinEngineCyclesPhenom.locationPositionId,
            lifeCodeId: twinEngineCyclesPhenom.lifeCodeId,
            usage: cycles,
          });
        }
      }
    }

    return [
      {
        flightTypeId,
        departurePlaceId,
        arrivalPlaceId,
        takeOffTimeUtc: sector.takeoffTime
          ? new Date(sector.takeoffTime).toISOString()
          : undefined,
        landingTimeUtc: sector.landingTime
          ? new Date(sector.landingTime).toISOString()
          : undefined,
        flightHours: flightMinutes,
        landings: fsLandings,
        cycles,
        lifeCodes: lifeCodes.length > 0 ? lifeCodes : undefined,
      },
    ];
  },
};

export default techlogFlightToRusadaSchema;
